import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, Reveal, BtnGhost } from "@/components/Bits";
import { CATEGORIES } from "@/data/categories";

export default function Services() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CATEGORIES;
    return CATEGORIES.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        c.sub_categories.some((s) => s.toLowerCase().includes(needle))
    );
  }, [q]);

  return (
    <div data-testid="services-page">
      <SEO
        title="Services"
        description="Explore all 26 Middle Man service categories and 208 subcategories — errands, grocery, food & medicine pickup, courier, documents, government work, elderly care, agriculture and more."
        path="/services"
      />
      <PageHero
        kicker="Services"
        title="26 categories. 208 subcategories."
        subtitle="Everyday work, covered — from medicine pickup and grocery runs to government office work, elderly assistance and farm labour."
      />

      <div className="mx-auto max-w-7xl px-6 py-14 md:py-20">
        <Reveal>
          <div className="relative max-w-xl">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-mute" />
            <input
              data-testid="services-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a service — e.g. medicine, documents, pet care"
              className="w-full border border-line bg-white py-3.5 pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-mute/50 focus:border-brand"
            />
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {filtered.map((c, i) => (
            <Reveal key={c.id} delay={(i % 6) * 0.04}>
              <article
                data-testid={`service-card-${c.id}`}
                className="flex h-full flex-col border border-line bg-white p-7 transition-colors duration-300 hover:border-brand"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-line bg-brand-soft">
                    <c.Icon className="h-5 w-5 text-brand" strokeWidth={1.7} />
                  </span>
                  <span className="font-mono text-[11px] text-mute">
                    {c.price_max != null ? `₹${c.price_min}–${c.price_max} · ${c.price_unit_label}` : "mutually decided"}
                  </span>
                </div>
                <h2 className="mt-5 font-serif text-xl font-semibold leading-snug text-ink">{c.name}</h2>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.sub_categories.map((s) => (
                    <span key={s} className="border border-line bg-cream px-2 py-1 text-[11px] font-medium text-mute">
                      {s}
                    </span>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mt-16 text-center text-sm text-mute" data-testid="services-empty">
            No categories match “{q}”. Try another search.
          </p>
        )}

        <Reveal className="mt-16">
          <div className="flex flex-wrap items-center gap-5 border border-line bg-creamalt p-8">
            <p className="max-w-xl flex-1 text-sm leading-relaxed text-mute">
              All prices are indicative Middle Man service charges only. See the full recommended-charges table and
              what's excluded before you book.
            </p>
            <BtnGhost to="/pricing" testid="services-pricing-btn">See recommended charges</BtnGhost>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
